import { useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { DatePicker, Form, Select, Tooltip } from "antd";
import { BiSlider } from "react-icons/bi";
import styled from "styled-components";
import moment, { Moment } from "moment";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useFilters } from "core/features/filters/store";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { SELECT_POPUP_Z_INDEX } from "../utils";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import {
  formatRdoDate,
  getRdoDateMoments,
  RdoDataModo,
} from "./diario/rdoDateRange";

type Props = {
  filtersData?: FiltrosAvancado;
};

const { Item, useForm } = Form;

const RelatorioPopoverContent = PopoverContent as any;
const RelatorioForm = Form as any;
const RelatorioPopoverHeader = PopoverHeader as any;
const RelatorioPopoverBody = PopoverBody as any;
const RelatorioPopoverFooter = PopoverFooter as any;
const RelatorioStack = Stack as any;
const RelatorioButton = Button as any;

const DATA_MODO_OPTIONS = [
  { label: "Geral (todo o período)", value: "geral" },
  { label: "Esta semana", value: "semana" },
  { label: "Este mês", value: "mes" },
  { label: "Personalizado", value: "personalizado" },
];

function resolveInheritedPeriodoIds(
  currentIds: string[] | undefined,
  safraPeriodo: string | undefined,
  availableIds: string[]
): string[] {
  if (currentIds?.length) {
    return currentIds.filter((id) => availableIds.includes(id));
  }
  if (safraPeriodo && availableIds.includes(safraPeriodo)) {
    return [safraPeriodo];
  }
  return [];
}

export function FiltroAvancadoDiarioOperacoes({ filtersData }: Props) {
  const [form] = useForm();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { filters, updateField } = useFilters();
  const expandScreen = useRelatorioContext((s) => s.expandScreen);
  const { filtersAdvanced } = filters;
  const { hasPermission } = useValidatePermission();
  const permissionRdo = hasPermission(["Relatorios.RelatorioDiarioOperacoesAgricolas"]);

  const periodoOptions = useMemo(
    () =>
      (filtersData?.safraPeriodos || []).map((item: any) => ({
        label: item.nome,
        value: item.safraPeriodoId,
      })),
    [filtersData?.safraPeriodos]
  );

  const availableIds = useMemo(
    () => periodoOptions.map((o) => o.value),
    [periodoOptions]
  );

  const [dataModo, setDataModo] = useState<RdoDataModo>(
    filtersAdvanced?.rdoDataModo || "geral"
  );

  const activeFiltersCount = [
    (filtersAdvanced?.safraPeriodoIds?.length ?? 0) > 0,
    filtersAdvanced?.rdoDataModo && filtersAdvanced.rdoDataModo !== "geral",
  ].filter(Boolean).length;

  const selectPopupProps = expandScreen
    ? {
        dropdownStyle: { zIndex: SELECT_POPUP_Z_INDEX },
        getPopupContainer: () => document.body,
      }
    : {};

  function resolveFormDates(modo: RdoDataModo): {
    rdoDataInicio: Moment | null;
    rdoDataFim: Moment | null;
  } {
    if (modo === "semana" || modo === "mes") {
      const { inicio, fim } = getRdoDateMoments(modo);
      return { rdoDataInicio: inicio, rdoDataFim: fim };
    }

    if (modo === "personalizado") {
      return {
        rdoDataInicio: filtersAdvanced?.rdoDataInicio
          ? moment(filtersAdvanced.rdoDataInicio)
          : null,
        rdoDataFim: filtersAdvanced?.rdoDataFim
          ? moment(filtersAdvanced.rdoDataFim)
          : null,
      };
    }

    return { rdoDataInicio: null, rdoDataFim: null };
  }

  function handleDataModoChange(value: RdoDataModo) {
    setDataModo(value);

    if (value === "semana" || value === "mes") {
      const { inicio, fim } = getRdoDateMoments(value);
      form.setFieldsValue({
        rdoDataModo: value,
        rdoDataInicio: inicio,
        rdoDataFim: fim,
      });
      return;
    }

    if (value === "geral") {
      form.setFieldsValue({
        rdoDataModo: value,
        rdoDataInicio: null,
        rdoDataFim: null,
      });
      return;
    }

    // Personalizado: mantém datas já visíveis (ex.: veio de semana/mês) ou as do store
    const current = form.getFieldsValue(["rdoDataInicio", "rdoDataFim"]);
    form.setFieldsValue({
      rdoDataModo: value,
      rdoDataInicio:
        current.rdoDataInicio ??
        (filtersAdvanced?.rdoDataInicio
          ? moment(filtersAdvanced.rdoDataInicio)
          : null),
      rdoDataFim:
        current.rdoDataFim ??
        (filtersAdvanced?.rdoDataFim
          ? moment(filtersAdvanced.rdoDataFim)
          : null),
    });
  }

  function syncFormFromStore() {
    const inheritedIds = resolveInheritedPeriodoIds(
      filtersAdvanced?.safraPeriodoIds,
      filtersAdvanced?.safraPeriodo,
      availableIds
    );

    const modo = (filtersAdvanced?.rdoDataModo || "geral") as RdoDataModo;
    setDataModo(modo);
    form.setFieldsValue({
      safraPeriodoIds: inheritedIds,
      rdoDataModo: modo,
      ...resolveFormDates(modo),
    });

    if (
      (!filtersAdvanced?.safraPeriodoIds?.length) &&
      inheritedIds.length &&
      filtersAdvanced?.safraPeriodo
    ) {
      updateField("filtersAdvanced", {
        ...filtersAdvanced,
        safraPeriodoIds: inheritedIds,
        rdoDataModo: filtersAdvanced?.rdoDataModo || "geral",
        rdoDataInicio: filtersAdvanced?.rdoDataInicio ?? null,
        rdoDataFim: filtersAdvanced?.rdoDataFim ?? null,
      });
    }
  }

  const safraPeriodoCtx = filtersAdvanced?.safraPeriodo || "";
  const safraPeriodoIdsKey = (filtersAdvanced?.safraPeriodoIds || []).join("|");
  const availableIdsKey = availableIds.join("|");

  // Herda safraPeriodo do contexto → safraPeriodoIds na 1ª entrada (sem abrir o popover)
  useEffect(() => {
    if (!permissionRdo || !availableIds.length) return;

    const inheritedIds = resolveInheritedPeriodoIds(
      filtersAdvanced?.safraPeriodoIds,
      filtersAdvanced?.safraPeriodo,
      availableIds
    );

    if (
      (filtersAdvanced?.safraPeriodoIds || []).length ||
      !inheritedIds.length ||
      !filtersAdvanced?.safraPeriodo
    ) {
      return;
    }

    updateField("filtersAdvanced", {
      ...filtersAdvanced,
      safraPeriodoIds: inheritedIds,
      rdoDataModo: filtersAdvanced?.rdoDataModo || "geral",
      rdoDataInicio: filtersAdvanced?.rdoDataInicio ?? null,
      rdoDataFim: filtersAdvanced?.rdoDataFim ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionRdo, availableIdsKey, safraPeriodoCtx, safraPeriodoIdsKey]);

  useEffect(() => {
    if (isOpen) {
      syncFormFromStore();
    }
  }, [isOpen, availableIds.join("|")]);

  function handleFiltrar() {
    form.validateFields().then((values) => {
      const modo = (values.rdoDataModo || "geral") as RdoDataModo;
      const inicio: Moment | null = values.rdoDataInicio || null;
      const fim: Moment | null = values.rdoDataFim || null;
      updateField("filtersAdvanced", {
        ...filtersAdvanced,
        safraPeriodoIds: values.safraPeriodoIds || [],
        rdoDataModo: modo,
        rdoDataInicio:
          modo === "personalizado" && inicio ? formatRdoDate(inicio) : null,
        rdoDataFim:
          modo === "personalizado" && fim ? formatRdoDate(fim) : null,
      });
      onClose();
    });
  }

  function handleLimpar() {
    const inherited = resolveInheritedPeriodoIds(
      undefined,
      filtersAdvanced?.safraPeriodo,
      availableIds
    );
    setDataModo("geral");
    form.setFieldsValue({
      safraPeriodoIds: inherited,
      rdoDataModo: "geral",
      rdoDataInicio: null,
      rdoDataFim: null,
    });
    updateField("filtersAdvanced", {
      ...filtersAdvanced,
      safraPeriodoIds: inherited,
      rdoDataModo: "geral",
      rdoDataInicio: null,
      rdoDataFim: null,
    });
  }

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <ButtonSliderWrapper disabled={!permissionRdo}>
          {activeFiltersCount > 0 && <IconCircle>{activeFiltersCount}</IconCircle>}
          <BiSlider />
        </ButtonSliderWrapper>
      </PopoverTrigger>
      <RelatorioPopoverContent p={4} w="sm">
        <PopoverArrow />
        <PopoverCloseButton />
        <RelatorioPopoverHeader fontWeight="bold">Filtros</RelatorioPopoverHeader>
        <RelatorioForm form={form} layout="vertical">
          <RelatorioPopoverBody>
            <RelatorioStack spacing={2}>
              <Wrap>
                <Item
                  label="Período de Produção"
                  name="safraPeriodoIds"
                  rules={[
                    { required: true, message: "Período de Produção é obrigatório" },
                  ]}
                >
                  <SelectAntd
                    {...selectPopupProps}
                    mode="multiple"
                    allowClear
                    showSearch
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    placeholder="Selecione um ou mais"
                    disabled={!permissionRdo}
                    options={periodoOptions}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip
                        overlayStyle={
                          expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                        }
                        title={omittedValues.map((v) => v.label).join(", ")}
                        placement="top"
                      >
                        +{omittedValues.length} itens
                      </Tooltip>
                    )}
                  />
                </Item>
              </Wrap>

              <Wrap>
                <Item label="Data" name="rdoDataModo">
                  <SelectAntd
                    {...selectPopupProps}
                    placeholder="Data"
                    disabled={!permissionRdo}
                    options={DATA_MODO_OPTIONS}
                    onChange={(value) =>
                      handleDataModoChange(value as RdoDataModo)
                    }
                  />
                </Item>
              </Wrap>

              {dataModo !== "geral" && (
                <>
                  <Wrap>
                    <Item
                      label="Data início"
                      name="rdoDataInicio"
                      rules={
                        dataModo === "personalizado"
                          ? [
                              {
                                required: true,
                                message: "Data início é obrigatória",
                              },
                            ]
                          : undefined
                      }
                    >
                      <DatePickerAntd
                        format="DD/MM/YYYY"
                        placeholder="Data início"
                        disabled={
                          !permissionRdo ||
                          dataModo === "semana" ||
                          dataModo === "mes"
                        }
                        style={{ width: "100%" }}
                        getPopupContainer={
                          expandScreen ? () => document.body : undefined
                        }
                      />
                    </Item>
                  </Wrap>
                  <Wrap>
                    <Item
                      label="Data fim"
                      name="rdoDataFim"
                      rules={
                        dataModo === "personalizado"
                          ? [
                              {
                                required: true,
                                message: "Data fim é obrigatória",
                              },
                            ]
                          : undefined
                      }
                    >
                      <DatePickerAntd
                        format="DD/MM/YYYY"
                        placeholder="Data fim"
                        disabled={
                          !permissionRdo ||
                          dataModo === "semana" ||
                          dataModo === "mes"
                        }
                        style={{ width: "100%" }}
                        getPopupContainer={
                          expandScreen ? () => document.body : undefined
                        }
                      />
                    </Item>
                  </Wrap>
                </>
              )}
            </RelatorioStack>
          </RelatorioPopoverBody>

          <RelatorioPopoverFooter display="flex" justifyContent="space-between">
            <RelatorioButton variant="ghost" onClick={handleLimpar} disabled={!permissionRdo}>
              Limpar
            </RelatorioButton>
            <RelatorioButton
              colorScheme="green"
              onClick={handleFiltrar}
              disabled={!permissionRdo}
            >
              Filtrar
            </RelatorioButton>
          </RelatorioPopoverFooter>
        </RelatorioForm>
      </RelatorioPopoverContent>
    </Popover>
  );
}

const ButtonSliderWrapper = styled.button`
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 45px;
  height: 60px;
  position: relative;

  svg {
    font-size: 20px;
  }

  :active {
    background: #20a45a;
    border-color: #20a45a;
  }

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Wrap = styled.div`
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-col {
    padding: 0;
  }
`;

const SelectAntd = styled(Select)`
  width: 100% !important;

  .ant-select-selector {
    min-height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }

  &.ant-select-multiple > .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
    outline: none !important;
  }
  &.ant-select-multiple > .ant-select-selector:hover {
    border: 1px solid #40a9ff !important;
  }
  &.ant-select-multiple.ant-select-open > .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }
`;

const DatePickerAntd = styled(DatePicker)`
  width: 100% !important;
  height: 40px !important;
  border-radius: 8px !important;
`;

const IconCircle = styled.span`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 2px;
  right: 1px;
  border-radius: 50%;
  background-color: #ff0000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;
